import { Link } from 'react-router-dom';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a18] via-[#0d0d24] to-[#0a0a18]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a18]/90 backdrop-blur-sm border-b border-[#1a1a3a]">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/dartsScheduler-trn.png" alt="Darts Scheduler" className="h-8" />
          </Link>
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="card">
            <h1 className="text-3xl font-bold text-white mb-2">Privacy Policy</h1>
            <p className="text-gray-500 mb-8">Last updated: January 2025</p>

            <div className="prose prose-invert max-w-none space-y-6 text-gray-300">
              <section>
                <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
                <p>
                  Darts Scheduler ("we", "our", or "us") is committed to protecting your privacy.
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                  when you use our service.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>

                <h3 className="text-lg font-medium text-white mt-4 mb-2">Personal Information</h3>
                <p>When you create an account, we collect:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Email address</li>
                  <li>Name or nickname (as you choose to display)</li>
                  <li>Profile information you choose to provide</li>
                  <li>Timezone preferences</li>
                </ul>

                <h3 className="text-lg font-medium text-white mt-4 mb-2">Usage Information</h3>
                <p>We automatically collect:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Game scheduling and results data</li>
                  <li>Availability preferences</li>
                  <li>Device and browser information</li>
                  <li>Log data and usage patterns</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Provide and maintain the Service</li>
                  <li>Process game bookings and scheduling</li>
                  <li>Send notifications about your games</li>
                  <li>Display your public profile to other users</li>
                  <li>Improve and personalize the Service</li>
                  <li>Communicate with you about updates and features</li>
                  <li>Ensure security and prevent fraud</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">4. Information Sharing</h2>
                <p>We may share your information with:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li><strong>Other users:</strong> Your public profile information is visible to other users</li>
                  <li><strong>Service providers:</strong> Third parties that help us operate the Service</li>
                  <li><strong>Legal requirements:</strong> When required by law or to protect our rights</li>
                </ul>
                <p className="mt-3">
                  We do not sell your personal information to third parties.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">5. Data Security</h2>
                <p>
                  We implement appropriate technical and organizational measures to protect your information, including:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Encryption of data in transit (HTTPS)</li>
                  <li>Secure password hashing</li>
                  <li>Regular security assessments</li>
                  <li>Access controls and authentication</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">6. Your Rights</h2>
                <p>You have the right to:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Access your personal data</li>
                  <li>Correct inaccurate data</li>
                  <li>Delete your account and associated data</li>
                  <li>Export your data</li>
                  <li>Opt out of marketing communications</li>
                </ul>
                <p className="mt-3">
                  You can manage most of these through your account Settings, or contact us for assistance.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">7. Cookies and Tracking</h2>
                <p>
                  We use cookies and similar technologies to:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Keep you signed in</li>
                  <li>Remember your preferences</li>
                  <li>Analyze usage patterns</li>
                  <li>Improve the Service</li>
                </ul>
                <p className="mt-3">
                  You can control cookies through your browser settings, though this may affect functionality.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">8. Data Retention</h2>
                <p>
                  We retain your information for as long as your account is active or as needed to provide
                  the Service. You can delete your account at any time, and we will remove your data within
                  30 days, except where retention is required by law.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">9. Children's Privacy</h2>
                <p>
                  The Service is not intended for users under 13 years of age. We do not knowingly collect
                  information from children under 13.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">10. International Users</h2>
                <p>
                  If you are accessing the Service from outside the United Kingdom, please be aware that
                  your information may be transferred to, stored, and processed in the UK where our servers
                  are located.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">11. Changes to This Policy</h2>
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes
                  by posting the new policy on this page and updating the "Last updated" date.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-semibold text-white mb-3">12. Contact Us</h2>
                <p>
                  If you have questions about this Privacy Policy, please contact us at{' '}
                  <a href="mailto:privacy@dartscheduler.com" className="text-[#1111d4] hover:underline">
                    privacy@dartscheduler.com
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
